import { BLOG_REVIEW_LEAD, blogDraftReadOnlyHtml, blogDraftReviewHtml, blogDraftViewerHtml } from './blog-draft-preview';
import { EMAIL_REVIEW_LEAD, emailReviewMessage, emailViewDocument, parseSentEmailReview, readOnlyViewDocument, type SentReviewRound } from './email-review';
import type { MessageReferences, SocialPostDraftCard } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { MessageChoices } from './message-choices';
import type { ReviewComment, SentReviewComment } from './site-review';

export type DraftKind = 'EMAIL' | 'BLOG_POST' | 'SOCIAL_POST';

export interface DraftPreviewCardData {
  workspaceId: string;
  taskId: string;
  draftId: string;
  kind: DraftKind;
  title: string;
  subject: string;
  spaceSlug: string;
  post?: SocialPostDraftCard;
}

export interface DraftPreview {
  html: string;
  kind: DraftKind | null;
  title: string;
  subject: string;
  preheader: string;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG = /^[a-z0-9][a-z0-9-]{0,127}$/i;
const MAX_TEXT = 1000;
const MAX_ID = 128;

function text(value: unknown, cap: number): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, cap) : '';
}

export function isDraftBodyPath(key: string): boolean {
  return key.includes('/.drafts/') || key.startsWith('.drafts/');
}

export function draftKind(value: unknown): DraftKind | null {
  return value === 'EMAIL' || value === 'BLOG_POST' || value === 'SOCIAL_POST' ? value : null;
}

export function readDraftPreviewCard(metadata: unknown): DraftPreviewCardData | null {
  const raw = (metadata as { draftPreviewCard?: unknown } | null | undefined)?.draftPreviewCard;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const card = raw as Record<string, unknown>;
  if (card.type !== undefined && card.type !== 'draft_preview') return null;
  const workspaceId = text(card.workspaceId, MAX_ID);
  const taskId = text(card.taskId, MAX_ID);
  const draftId = text(card.draftId, MAX_ID);
  const kind = draftKind(card.kind);
  if (!GUID.test(workspaceId) || !GUID.test(taskId) || !GUID.test(draftId) || !kind) return null;
  const spaceSlug = text(card.spaceSlug, MAX_ID);
  return {
    workspaceId,
    taskId,
    draftId,
    kind,
    title: text(card.title, MAX_TEXT),
    subject: kind === 'EMAIL' ? text(card.subject, MAX_TEXT) : '',
    spaceSlug: SLUG.test(spaceSlug) ? spaceSlug : '',
  };
}

export function draftSpaceSlug(card: DraftPreviewCardData, workspaces: readonly { id?: string; slug?: string }[]): string {
  if (card.spaceSlug) return card.spaceSlug;
  const slug = workspaces.find((w) => w.id === card.workspaceId)?.slug ?? '';
  return SLUG.test(slug) ? slug : '';
}

export function draftHeadline(card: DraftPreviewCardData, preview?: DraftPreview): string {
  const loaded = preview ? (card.kind === 'EMAIL' ? preview.subject || preview.title : preview.title) : '';
  const own = card.kind === 'EMAIL' ? card.subject || card.title : card.title;
  return text(loaded, MAX_TEXT) || own;
}

function blogPreview(preview: DraftPreview, title: string) {
  return { title, html: preview.html, heroImageUrl: '' };
}

export function draftReviewName(card: DraftPreviewCardData, preview?: DraftPreview): string {
  return card.kind === 'EMAIL' ? text(preview?.subject, MAX_TEXT) || card.subject : text(preview?.title, MAX_TEXT) || card.title;
}

export function draftReviewHtml(card: DraftPreviewCardData, preview: DraftPreview): string {
  return card.kind === 'EMAIL' ? preview.html : blogDraftReviewHtml(blogPreview(preview, preview.title || card.title), preview.title || card.title);
}

export function draftViewHtml(card: DraftPreviewCardData, preview: DraftPreview): string {
  return card.kind === 'EMAIL' ? emailViewDocument(preview.html) : blogDraftViewerHtml(blogPreview(preview, preview.title || card.title), preview.title || card.title);
}

export function draftReadOnlyHtml(card: DraftPreviewCardData, preview: DraftPreview): string {
  return card.kind === 'EMAIL' ? readOnlyViewDocument(preview.html) : blogDraftReadOnlyHtml(blogPreview(preview, preview.title || card.title), preview.title || card.title);
}

export type DraftActionKind = DraftKind | 'SOCIAL_POST';
export type DraftTapAction = 'DELIVER' | 'SCHEDULE' | 'DROP' | 'EDIT';

export interface DraftAction {
  type: 'TAP';
  kind: DraftActionKind;
  spaceSlug: string;
  taskId: string;
  draftId?: string;
  action?: DraftTapAction;
  at?: string;
}

export interface DraftMessage {
  text: string;
  draftAction: DraftAction;
}

const TAP_TEXT: Record<DraftKind, { deliver: string; schedule: string; drop: string }> = {
  EMAIL: { deliver: 'Send the email draft', schedule: 'Schedule the email draft', drop: 'Drop the email draft' },
  BLOG_POST: { deliver: 'Publish the blog post draft', schedule: 'Schedule the blog post draft', drop: 'Drop the blog post draft' },
  SOCIAL_POST: { deliver: 'Post the social post draft', schedule: 'Schedule the social post draft', drop: 'Drop the social post draft' },
};

export function draftActionMessage(card: Pick<DraftPreviewCardData, 'kind' | 'taskId' | 'draftId'>, spaceSlug: string): DraftMessage {
  return {
    text: TAP_TEXT[card.kind].deliver,
    draftAction: {
      type: 'TAP',
      kind: card.kind,
      spaceSlug,
      taskId: card.taskId,
      draftId: card.draftId,
      ...(card.kind === 'SOCIAL_POST' ? { action: 'DELIVER' as const } : {}),
    },
  };
}

export function draftReferences(action: DraftAction): MessageReferences {
  return { draftAction: action } as MessageReferences;
}

export function draftScheduleMessage(card: Pick<DraftPreviewCardData, 'kind' | 'taskId' | 'draftId'>, spaceSlug: string, publishAt: string): DraftMessage {
  return {
    text: TAP_TEXT[card.kind].schedule,
    draftAction: { type: 'TAP', kind: card.kind, spaceSlug, taskId: card.taskId, draftId: card.draftId, action: 'SCHEDULE', at: publishAt },
  };
}

export const DRAFT_SCHEDULE_LEAD_MS = 2 * 60_000;

export type DraftButton = { kind: 'schedule'; at: string } | { kind: 'scheduled'; at: string } | { kind: 'deliver' };

export function draftScheduleCutoff(state: { delivered: unknown; publishAt: string | null; scheduled: unknown } | undefined): number | null {
  if (!state || state.delivered || state.scheduled || !state.publishAt) return null;
  const planned = Date.parse(state.publishAt);
  return Number.isFinite(planned) ? planned - DRAFT_SCHEDULE_LEAD_MS : null;
}

export function draftButtonOf(
  state: { delivered: { scheduled: boolean } | null; publishAt: string | null; scheduled: { at: string } | null } | undefined,
  now: number,
): DraftButton {
  if (!state || state.delivered) return { kind: 'deliver' };
  if (state.scheduled) return { kind: 'scheduled', at: state.scheduled.at };
  const planned = state.publishAt ? Date.parse(state.publishAt) : NaN;
  return Number.isFinite(planned) && planned - now > DRAFT_SCHEDULE_LEAD_MS && state.publishAt ? { kind: 'schedule', at: state.publishAt } : { kind: 'deliver' };
}

export function draftDropMessage(card: Pick<DraftPreviewCardData, 'kind' | 'taskId' | 'draftId'>, spaceSlug: string): DraftMessage {
  return {
    text: TAP_TEXT[card.kind].drop,
    draftAction: { type: 'TAP', kind: card.kind, spaceSlug, taskId: card.taskId, draftId: card.draftId, action: 'DROP' },
  };
}

export function socialPostTapMessage(action: 'edit' | 'drop', spaceSlug: string, taskId: string): DraftMessage {
  return {
    text: action === 'edit' ? 'Edit the social post' : 'Drop the scheduled post',
    draftAction: { type: 'TAP', kind: 'SOCIAL_POST', spaceSlug, taskId, action: action === 'edit' ? 'EDIT' : 'DROP' },
  };
}

export type DraftCardButton = 'schedule' | 'deliverNow' | 'editFirst' | 'drop' | 'view' | 'viewPost' | 'edit' | 'publishChanges';

export interface DraftCardActions {
  status?: { key: string; when?: string };
  buttons: DraftCardButton[];
  statusAt?: number;
  done?: { at?: string; url?: string };
}

export function draftCardActions(
  kind: DraftKind,
  state:
    | {
        delivered: { url: string; scheduled: boolean; at: string; changesNotLive: boolean } | null;
        publishAt: string | null;
        scheduled: { at: string } | null;
        working: boolean;
        ended: boolean;
      }
    | undefined,
  now: number,
  revising = false,
  publishTime = false,
): DraftCardActions {
  if (!state) return { buttons: [] };
  const delivered = state.delivered;
  if (kind === 'SOCIAL_POST' && delivered && !delivered.scheduled) {
    return { buttons: [], done: { ...(delivered.at ? { at: delivered.at } : {}), ...(delivered.url ? { url: delivered.url } : {}) } };
  }
  if (state.ended) return { buttons: [] };
  if (delivered && !delivered.scheduled) {
    const changesNotLive = kind === 'BLOG_POST' && delivered.changesNotLive;
    if (!publishTime) {
      if (kind === 'EMAIL') return { status: { key: 'sendHold.sentNow' }, buttons: [] };
      if (changesNotLive) return { status: { key: 'draftPreview.changesNotLive' }, buttons: delivered.url ? ['publishChanges', 'viewPost'] : ['publishChanges'], statusAt: 1 };
      return { status: { key: 'draftPreview.published' }, buttons: delivered.url ? ['viewPost'] : [], statusAt: 0 };
    }
    if (changesNotLive) {
      return { status: { key: 'draftPreview.publishedAt', ...(delivered.at ? { when: delivered.at } : {}) }, buttons: delivered.url ? ['view', 'publishChanges'] : ['publishChanges'] };
    }
    return { buttons: [], done: { ...(delivered.at ? { at: delivered.at } : {}), ...(delivered.url ? { url: delivered.url } : {}) } };
  }
  if (state.working) return { status: { key: revising ? 'draftPreview.revising' : 'draftPreview.working' }, buttons: [] };
  if (delivered?.scheduled || state.scheduled) {
    const at = state.scheduled?.at ?? delivered?.at;
    return { status: { key: 'draftPreview.scheduledFor', ...(at ? { when: at } : {}) }, buttons: ['editFirst', 'drop'] };
  }
  const button = draftButtonOf(state, now);
  return { buttons: [button.kind === 'schedule' ? 'schedule' : 'deliverNow', 'editFirst', 'drop'] };
}

export function draftWhen(at: string, locale: string | undefined): string {
  const ms = Date.parse(at);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleString(locale, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export function draftDownloadName(card: DraftPreviewCardData, preview: DraftPreview): string {
  return draftFileName(card, text(preview.title, MAX_TEXT) || card.title || draftHeadline(card, preview));
}

export function draftFileName(card: DraftPreviewCardData, headline: string): string {
  const name = text(headline, 120).replace(/[\\/:*?"<>|]/g, '').trim();
  return `${name || (card.kind === 'EMAIL' ? 'Email' : 'Blog post')}.html`;
}

const SPACE_LINE = /^Space: (\S+)$/;
const TASK_LINE = /^Task: (\S+)$/;
const DRAFT_LINE = /^Draft: (\S+)$/;
const SUBJECT_LINE = /^Subject: .*$/;
const TITLE_LINE = /^Title: .*$/;

function leadFor(kind: DraftKind): string {
  return kind === 'EMAIL' ? EMAIL_REVIEW_LEAD : BLOG_REVIEW_LEAD;
}

export function draftReviewMessage(
  draft: { kind: DraftKind; taskId: string; draftId: string; spaceSlug: string; name: string },
  comments: readonly ReviewComment[],
): string {
  const name = text(draft.name, MAX_TEXT);
  const named = name ? [draft.kind === 'EMAIL' ? `Subject: ${name}` : `Title: ${name}`] : [];
  const head = [leadFor(draft.kind), `Space: ${draft.spaceSlug}`, `Task: ${draft.taskId}`, `Draft: ${draft.draftId}`, ...named];
  const [, ...blocks] = emailReviewMessage({ metaSiteId: '-', campaignId: draft.draftId, subject: '' }, comments).split('\n\n');
  return [head.join('\n'), ...blocks].join('\n\n');
}

function field(value: unknown, cap: number): string | null {
  return typeof value === 'string' && value.length <= cap ? value : null;
}

function object(value: unknown): Record<string, unknown> | null {
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
}

const TAP_ACTIONS: readonly DraftTapAction[] = ['DELIVER', 'SCHEDULE', 'DROP', 'EDIT'];

export function readDraftAction(metadata: unknown): DraftAction | null {
  const own = object(metadata);
  const action = object(own?.draftAction ?? object(own?.references)?.draftAction);
  if (!action) return null;
  const kind = action.kind === 'SOCIAL_POST' ? 'SOCIAL_POST' : draftKind(action.kind);
  const taskId = field(action.taskId, MAX_ID);
  const draftId = field(action.draftId, MAX_ID);
  const spaceSlug = field(action.spaceSlug, MAX_ID);
  const tap = action.action === undefined ? undefined : TAP_ACTIONS.find((known) => known === action.action);
  const at = typeof action.at === 'string' && Number.isFinite(Date.parse(action.at)) ? action.at : undefined;
  if (action.type !== 'TAP' || !kind || !taskId || spaceSlug === null || (action.action !== undefined && !tap)) return null;
  if (kind === 'SOCIAL_POST' && !draftId ? tap !== 'EDIT' && tap !== 'DROP' : !draftId || (kind === 'SOCIAL_POST' && (!tap || tap === 'EDIT'))) return null;
  if (tap === 'SCHEDULE' && !at) return null;
  return {
    type: 'TAP',
    kind,
    spaceSlug,
    taskId,
    ...(draftId ? { draftId } : {}),
    ...(tap ? { action: tap } : {}),
    ...(tap === 'SCHEDULE' && at ? { at } : {}),
  };
}

export interface SentDraftReview {
  kind: DraftKind;
  taskId: string;
  draftId: string;
  comments: SentReviewComment[];
}

export function parseSentDraftReview(message: string): SentDraftReview | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  const lead = lines[0]?.trim();
  const kind: DraftKind | null = lead === EMAIL_REVIEW_LEAD ? 'EMAIL' : lead === BLOG_REVIEW_LEAD ? 'BLOG_POST' : null;
  const taskId = TASK_LINE.exec(lines[2] ?? '')?.[1];
  const draftId = DRAFT_LINE.exec(lines[3] ?? '')?.[1];
  if (!kind || !SPACE_LINE.test(lines[1] ?? '') || !taskId || !draftId) return null;
  const named = kind === 'EMAIL' ? SUBJECT_LINE : TITLE_LINE;
  const start = named.test(lines[4] ?? '') ? 5 : 4;
  const comments = parseSentEmailReview([EMAIL_REVIEW_LEAD, 'Site: -', 'Campaign: -', ...lines.slice(start)].join('\n'));
  return comments ? { kind, taskId, draftId, comments } : null;
}

export function readSentDraftReview(message: string): SentReviewRound | null {
  const sent = parseSentDraftReview(message);
  return sent ? { id: sent.draftId.toLowerCase(), comments: sent.comments } : null;
}

export function draftReviewPageUrl(draftId: string): string {
  return `https://draft-review.invalid/${encodeURIComponent(draftId)}`;
}

export function draftReviewKey(draftId: string, conversationId: string | null): string {
  return `draft:${draftId.toLowerCase()}|${conversationId ?? ''}`;
}

interface DraftCardRow {
  id?: string;
  finalId?: string;
  draftPreviewCard?: DraftPreviewCardData;
}

const rowKey = (row: DraftCardRow): string | undefined => row.finalId ?? row.id;
const draftKey = (card: DraftPreviewCardData): string => card.draftId.toLowerCase();

export function latestDraftCards<T extends DraftCardRow>(rows: readonly T[]): Map<string, T> {
  const latest = new Map<string, T>();
  for (const row of rows) {
    if (rowKey(row) && row.draftPreviewCard) latest.set(draftKey(row.draftPreviewCard), row);
  }
  return latest;
}

export function isLatestDraftCard(latest: ReadonlyMap<string, DraftCardRow>, row: DraftCardRow): boolean {
  const key = rowKey(row);
  const last = row.draftPreviewCard ? latest.get(draftKey(row.draftPreviewCard)) : undefined;
  return key !== undefined && last !== undefined && rowKey(last) === key;
}

export type DraftReviewSendPlan<T> = { status: 'send' } | { status: 'busy' } | { status: 'newer'; row: T; card: DraftPreviewCardData };

export function planDraftReviewSend<T extends DraftCardRow>(opened: T, latest: ReadonlyMap<string, T>, streaming: boolean): DraftReviewSendPlan<T> {
  const row = opened.draftPreviewCard ? latest.get(draftKey(opened.draftPreviewCard)) : undefined;
  if (row?.draftPreviewCard && !isLatestDraftCard(latest, opened)) return { status: 'newer', row, card: row.draftPreviewCard };
  return streaming ? { status: 'busy' } : { status: 'send' };
}

const DECISION_ASK = 'decision:';

export function isDraftActHeld(card: Pick<DraftPreviewCardData, 'kind'>, choices: MessageChoices | undefined): boolean {
  if (!choices?.askId.startsWith(DECISION_ASK)) return false;
  const act = `da:${choices.askId.slice(DECISION_ASK.length)}:${card.kind === 'EMAIL' ? 'send-campaign' : 'publish-post'}`;
  return choices.options.some((option) => option.id === act);
}
